import React, { useRef } from 'react';
import { FileUploadField as FileUploadFieldType } from '../../types/optionTypes';
import { Upload, X, FileText } from 'lucide-react';

interface FileUploadFieldProps {
    field: FileUploadFieldType;
    value: any;
    onChange: (value: any) => void;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({ field, value, onChange }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const files: File[] = Array.isArray(value) ? value : (value ? [value] : []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            if (field.multiple_upload) {
                onChange([...files, ...newFiles]);
            } else {
                onChange(newFiles[0]);
            }
        }
    };

    const removeFile = (index: number) => {
        if (field.multiple_upload) {
            const newFiles = [...files];
            newFiles.splice(index, 1);
            onChange(newFiles);
        } else {
            onChange(null);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            if (field.multiple_upload) {
                onChange([...files, ...newFiles]);
            } else {
                onChange(newFiles[0]);
            }
        }
    };

    return (
        <div className={`col-span-${field.col}`}>
            {field.label && (
                <label className="block text-sm font-medium text-gray-800 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors bg-gray-50 cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileChange}
                    multiple={field.multiple_upload}
                    accept={field.file_types?.join(',')}
                />

                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 font-medium">
                    クリックまたはドラッグ＆ドロップでファイルをアップロード
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    {field.uploadSize && `最大サイズ: ${field.uploadSize}MB`}
                </p>
            </div>

            {files.length > 0 && (
                <div className="mt-3 space-y-2">
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md shadow-sm">
                            <div className="flex items-center overflow-hidden">
                                <FileText className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                                <span className="text-sm text-gray-700 truncate">{file.name}</span>
                                <span className="text-xs text-gray-400 ml-2">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-gray-400 hover:text-red-500 p-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {field.description && (
                <div className="mt-2 text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: field.description }} />
            )}
        </div>
    );
};
